import {
  BadRequestError,
  ForbiddenError,
  HttpRequestError,
  HttpThrottleError,
  MethodNotAllowedError,
  NotFoundError,
  ServerError,
  UnauthorizedError,
  UnprocessableContentError,
} from './errors';
import type { HttpErrorOptions, HttpRequestErrorOptions } from './errors';
import type { HttpResponse } from './types';

/**
 * Which failure a non-ok status IS.
 *
 * Separate from WHEN it reaches the caller. `throwOnError` is a delivery choice
 * made on the outermost layer and changes nothing here: internally a non-ok
 * status always becomes one of these, because the retry loop retries what
 * throws (TE4, TE10).
 */

/** `delay-seconds` per RFC 9110 section 10.2.3: one or more digits, nothing else. */
const DELAY_SECONDS = /^\d+$/u;

/**
 * The day name every `HTTP-date` form opens with (RFC 9110 section 5.6.7).
 *
 * Not a full date parser: it is the cheapest test that separates a date from
 * whatever else `Date.parse` would have accepted, and `Date.parse` still decides
 * whether what follows is a real one.
 */
const HTTP_DATE_OPENS_WITH = /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)/u;

/**
 * `retry-after` in milliseconds.
 *
 * Exported because the retry loop reads the same header off the same response,
 * and a second parser is a second answer to "what does this header say" -- the
 * one that forgets the HTTP-date spelling.
 *
 * The header comes in two spellings and the field is one number, which is the
 * point of having the field at all: the unit is stated once, here, instead of
 * at every call site. A delta-seconds value is a plain integer; the other
 * spelling is an HTTP date, and what a caller wants from it is the wait it
 * implies rather than the moment it names.
 *
 * THE GRAMMAR DECIDES, AND BOTH BRANCHES NEEDED SAYING SO. RFC 9110 section
 * 10.2.3 gives `Retry-After = HTTP-date / delay-seconds` with
 * `delay-seconds = 1*DIGIT`, and neither `Number` nor `Date.parse` is that
 * grammar. Both are wider, and everything they accept beyond it arrives here as
 * a wait somebody has to honour.
 *
 * `Number` was the first hole. Measured against the live pipeline: `'1e3'` became
 * sixteen minutes and forty seconds out of three characters, `'0x10'` sixteen
 * seconds, `'2.5'` two and a half. The empty string and whitespace read as `0`,
 * which was the expensive one -- a wait of nought skips the backoff and the
 * jitter in `retryDelayMsOf`, the one outcome the pause exists to prevent, and it
 * does so while looking like an answer rather than like a missing field.
 *
 * `Date.parse` is the second hole, and narrowing only the first would have moved
 * the bug rather than closed it. Outside the formats ECMA-262 mandates, its
 * fallback is implementation-defined, and V8's guesses at fragments. Measured on
 * Node 22: `'2.5'` is 2001-02-04, `'+5'` and `'5'` are 2001-04-30, `'-1'` is
 * 2000-12-31. All three sit in the past, so the floor below turns them straight
 * back into the zero wait this function was narrowed to eliminate.
 *
 * The guard is the property all three permitted date forms share: they begin with
 * a day name. Section 5.6.7 gives `IMF-fixdate = day-name "," SP ...`,
 * `rfc850-date = day-name-l "," SP ...` and `asctime-date = day-name SP ...`, and
 * `day-name-l` spells out the same seven words, so a three-letter prefix covers
 * every form. Case-sensitively, because the ABNF writes `%s"Mon"`.
 *
 * A value in neither grammar yields no field, and that is the safe side: no wait
 * leaves the caller's own backoff in charge, a wrong wait replaces it.
 *
 * A negative delay is unrepresentable rather than clamped, which is why the first
 * branch has no floor. The second keeps one, because a date in the past is a
 * legitimate way of saying "now".
 */
export function retryAfterMs(headers: Headers): number | undefined {
  const raw = headers.get('retry-after')?.trim();
  if (raw === undefined || raw === '') return undefined;

  if (DELAY_SECONDS.test(raw)) return Number(raw) * 1000;
  if (!HTTP_DATE_OPENS_WITH.test(raw)) return undefined;

  const parsedAt = Date.parse(raw);
  return Number.isNaN(parsedAt)
    ? undefined
    : Math.max(0, parsedAt - Date.now());
}

/**
 * The failure a non-ok response becomes.
 *
 * ER38 is the `default`: a status with no name of its own — a 409, a 451 — is
 * `HttpRequestError` itself. That is why the base of the response branch is
 * concrete. Naming it `NotFoundError` or `ServerError` instead would be a lie a
 * caller acts on, and leaving it unthrowable was what the abstract base did.
 *
 * `failureOptions` produces what the failure carries besides the response: the
 * DEFINITIVE options after the `before` handlers (ER10, RY21), plus which call
 * this was and how long it took (ER63, ER66). It is a parameter rather than
 * something rebuilt here because there is only one right answer and it lives in
 * the pipeline. Rebuilding a version from `method` and `url` would be a second
 * answer to that question, and the one that reports a method the handlers
 * already rewrote.
 *
 * A function, because the duration belongs to the moment the failure is built.
 *
 * The whole object flows into every branch below through `options`, so the nine
 * classes cannot disagree about it -- the 429 spreads the same record rather
 * than assembling its own.
 */
export function failureForStatus(
  method: string,
  url: string,
  response: Pick<HttpResponse, 'headers' | 'status' | 'statusText'>,
  body: unknown,
  failureOptions: () => HttpErrorOptions,
): HttpRequestError {
  const options: HttpRequestErrorOptions = {
    ...failureOptions(),
    body,
    headers: response.headers,
    status: response.status,
    statusText: response.statusText,
  };

  switch (response.status) {
    case 400:
      return new BadRequestError(method, url, options);
    case 401:
      return new UnauthorizedError(method, url, options);
    case 403:
      return new ForbiddenError(method, url, options);
    case 404:
      return new NotFoundError(method, url, options);
    case 405:
      return new MethodNotAllowedError(method, url, options);
    case 422:
      return new UnprocessableContentError(method, url, options);
    case 429:
      return new HttpThrottleError(method, url, {
        ...options,
        retryAfterMs: retryAfterMs(response.headers),
      });
    default:
      break;
  }

  // The whole 5xx range on one class, which is why `ServerError` reads its
  // status from the options rather than from its own name (ER15).
  if (response.status >= 500) return new ServerError(method, url, options);

  return new HttpRequestError(
    `request failed: ${method} ${url}`,
    method,
    url,
    options,
  );
}
